import React, { useState, useContext } from "react";
import Container from "../components/Container";
import Button from "../components/Button";
import AuthCard from "../components/AuthCard";
import AuthHeader from "../components/AuthHeader";
import InputField from "../components/InputField";
// import CheckboxField from "../components/CheckboxField";
import AuthLink from "../components/AuthLink";
import Divider from "../components/Divider";
import GoogleButton from "../components/GoogleButton";
import AppRedirectModal from "../components/AppRedirectModal";
import { toast } from "react-hot-toast";
import { useNavigate } from "react-router-dom";
import { AuthContext } from "../AuthContext";
import { FaEye, FaEyeSlash } from "react-icons/fa";
import { Link } from "react-router-dom";

export default function Login() {
  const [formData, setFormData] = useState({
    email: "",
    password: "",
    acceptTerms: false,
  });
  const [showRedirectModal, setShowRedirectModal] = useState(false);
  const [pendingApp, setPendingApp] = useState(null);
  const [showPassword, setShowPassword] = useState(false);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const navigate = useNavigate();
  const { loginUser, loginWithToken, loading, user, token } = useContext(AuthContext);

  // Gérer le token Google dans l'URL
  React.useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const googleToken = urlParams.get("token");
    const error = urlParams.get("error");

    if (error) {
      toast.error("Échec de l'authentification Google");
      navigate("/login", { replace: true });
    } else if (googleToken) {
      const handleGoogleLogin = async () => {
        const res = await loginWithToken(googleToken);
        if (res.ok) {
          toast.success("Connexion Google réussie");
          navigate("/applications", { replace: true });
        } else {
          toast.error("Erreur lors de la connexion Google");
        }
      };
      handleGoogleLogin();
    }
  }, [loginWithToken, navigate]);

  // If already authenticated (e.g., after register auto-login), skip login page
  React.useEffect(() => {
    // Ne pas rediriger s'il y a une app en attente
    const hasPendingApp = sessionStorage.getItem("pendingAppClick");
    if (user && !showRedirectModal && !hasPendingApp) {
      navigate("/applications", { replace: true });
    }
  }, [user, navigate, showRedirectModal]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const res = await loginUser({
      email: formData.email,
      password: formData.password,
    });
    if (res?.ok) {
      toast.success("Connexion réussie");

      // Vérifier si une application était en attente de clic
      const pendingAppData = sessionStorage.getItem("pendingAppClick");
      if (pendingAppData) {
        try {
          const app = JSON.parse(pendingAppData);

          // Récupérer le token depuis localStorage (plus fiable après connexion)
          const userToken = localStorage.getItem("token");
          if (!userToken) {
            console.error("Token non disponible après connexion");
            sessionStorage.removeItem("pendingAppClick");
            navigate("/applications");
            return;
          }

          // Afficher le modal de confirmation au lieu de rediriger automatiquement
          console.log("📱 Affichage du modal pour l'app:", app.name);
          const appWithToken = { ...app, token: userToken };
          setPendingApp(appWithToken);
          setShowRedirectModal(true);
          console.log("✅ Modal activé avec state:", {
            showRedirectModal: true,
            app: appWithToken,
          });
          return;
        } catch (error) {
          console.error("Erreur lors de la redirection automatique:", error);
          sessionStorage.removeItem("pendingAppClick");
        }
      }

      // Sinon, navigation normale vers /applications
      navigate("/applications");
    } else {
      let errorMessage = "Échec de connexion";
      if (res?.error) {
        try {
          const errorObj = JSON.parse(res.error);
          errorMessage = errorObj.message || errorMessage;
        } catch {
          errorMessage = res.error;
        }
      }
      toast.error(errorMessage);
      console.log("Login error response:", res);
    }
  };

  const handleContinueToApp = () => {
    if (pendingApp) {
      // Nettoyer le sessionStorage
      sessionStorage.removeItem("pendingAppClick");

      const redirectPath = pendingApp.redirect_path || "/dashboard";
      const redirectUrl = `${pendingApp.frontend_url}${redirectPath}?token=${pendingApp.token}`;

      console.log(
        "🚀 Ouverture de l'application dans un nouvel onglet:",
        redirectUrl
      );
      toast.success(`Ouverture de ${pendingApp.name}...`);

      // Ouvrir dans un nouvel onglet
      window.open(redirectUrl, "_blank", "noopener,noreferrer");

      // Fermer le modal et rediriger vers les applications
      setShowRedirectModal(false);
      setPendingApp(null);
      navigate("/applications");
    }
  };

  const handleBackToApps = () => {
    // Nettoyer le sessionStorage
    sessionStorage.removeItem("pendingAppClick");

    setShowRedirectModal(false);
    setPendingApp(null);
    navigate("/applications");
  };

  const handleGoogleLogin = () => {
    console.log("Google login");
  };

  console.log("🔍 État du modal:", { showRedirectModal, pendingApp });

  return (
    <>
      {showRedirectModal && pendingApp && (
        <AppRedirectModal
          app={pendingApp}
          onContinue={handleContinueToApp}
          onCancel={handleBackToApps}
        />
      )}
      <Container className="min-h-screen bg-gradient-to-br from-[#E9FBF8] via-white to-[#FFEFD8] bg-bg-login bg-contain bg-bottom flex items-center justify-center py-">
        <AuthCard>
          <AuthHeader />

          <form className="mt-4 space-y-4" onSubmit={handleSubmit}>
            <InputField
              label="Email"
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              required
            />

            <div>
              <label className="block text-[14px] font-medium text-[#1B1C33]">
                Mot de passe
              </label>
              <div className="relative mt-2.5">
                <input
                  type={showPassword ? "text" : "password"}
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  className="w-full rounded-[7px] border border-[#C1BECE] px-4 py-2 text-[14px] focus:outline-none focus:ring-2 focus:ring-[#FEA12B]/60 pr-10"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-[#1B1C33] hover:text-[#FEA12B] transition-colors"
                >
                  {showPassword ? (
                    <FaEyeSlash size={18} />
                  ) : (
                    <FaEye size={18} />
                  )}
                </button>
              </div>
              <div className="flex justify-end mt-2">
                <Link
                  to="/forgot-password"
                  className="text-[12px] font-medium text-[#FEA12B] hover:underline"
                >
                  Mot de passe oublié ?
                </Link>
              </div>
            </div>

            {/* <CheckboxField
            id="terms"
            label="J'accepte les"
            linkText="Conditions d'utilisation"
            checked={formData.acceptTerms}
            onChange={handleChange}
            name="acceptTerms"
          /> */}

            <div className="flex pt-5 justify-center">
              <Button
                type="submit"
                className="w-full py-2 text-[14px]"
                disabled={loading}
              >
                {loading ? "Connexion..." : "Se connecter"}
              </Button>
            </div>
          </form>

          <AuthLink
            text="Vous n'avez pas de"
            linkText="Compte ?"
            to="/register"
          />

          <Divider className="mt-6" />

          <GoogleButton onClick={handleGoogleLogin} />
        </AuthCard>
      </Container>
    </>
  );
}
